import { useEffect, useState } from 'react';
import {
    FormControl,
    Checkbox,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
} from '@mui/material';
import { getFilterOptionIcon, getUserFriendlyLabel } from '../filter-helpers';

export interface FilterGroupProps {
    name: string;
    filters: Record<string, boolean>;
    onUpdateFilters:
        | React.Dispatch<React.SetStateAction<Record<string, boolean>>>
        | ((filters: any) => void);
}

/** A dropdown menu containing checkboxes. */
export const FilterGroup = ({
    name,
    filters,
    onUpdateFilters,
}: FilterGroupProps) => {
    const [activeFilters, setActiveFilters] = useState<string[]>([]);

    useEffect(() => {
        if (filters) {
            setActiveFilters(getActiveFilters() ?? []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const getActiveFilters = (): string[] => {
        return Object.keys(filters).filter(
            (key: string) => filters[key] === true
        );
    };

    const onChange = (event: SelectChangeEvent<string[]>) => {
        const {
            target: { value },
        } = event;

        // On autofill we get a stringified value.
        const _value = typeof value === 'string' ? value.split(',') : value;

        // Create new filter object reflecting latest change.
        const newFilters: Record<string, boolean> = {};
        Object.keys(filters).forEach((filterName: string) => {
            if (_value.includes(filterName)) newFilters[filterName] = true;
            else newFilters[filterName] = false;
        });

        onUpdateFilters(newFilters);
    };

    return Object.keys(filters).length > 0 ? (
        <div data-testid={`FilterGroup-${name}`}>
            <FormControl
                sx={{ m: 1, flexGrow: 1, width: '200px' }}
                size="small"
            >
                <InputLabel>{name}</InputLabel>
                <Select
                    multiple
                    value={activeFilters}
                    label={name}
                    onChange={onChange}
                    renderValue={(selected) =>
                        `${name} (${selected.length}/${
                            Object.keys(filters).length
                        })`
                    }
                >
                    {Object.keys(filters).map((filterName: string) => {
                        return (
                            <MenuItem
                                key={filterName}
                                value={filterName}
                                data-testid="select-option"
                            >
                                <Checkbox
                                    checked={
                                        activeFilters.indexOf(filterName) > -1
                                    }
                                />

                                <span style={{ marginRight: '5px' }}>
                                    {getFilterOptionIcon(name, filterName)}
                                </span>

                                <ListItemText
                                    primary={getUserFriendlyLabel(
                                        name,
                                        filterName
                                    )}
                                />
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>
        </div>
    ) : (
        <></>
    );
};
